import jsPDF from 'jspdf';
import 'jspdf-autotable';
import _ from 'lodash';
import { inWords2 } from '../../utils';
import { roundMoney } from '../../utils/money';
import { baseUrl } from '../../config/api';

function getEntityShortCode(entityId) {
  return entityId === '62da46cf7eb6aa02db41297f'
    ? 'VO'
    : entityId === '62da46c57eb6aa02db41297d'
      ? 'IE'
      : `Astra Lenses Eyecare`;
}

/** QR print box (mm): must stay in sync with placement math below. */
const QR_SIZE_MM = 22;
const QR_MARGIN_MM = 2;

/**
 * Draws QR on the given page and returns lowest Y occupied (top origin), or 0 if skipped.
 * Uses content bottom so short thermal pages reserve enough height below footer/ads.
 */
async function addQrToSpecificPage(doc, { qr, qrCode, contentBottomMm, pageNumber }) {
  if (!qr || !qrCode || qrCode.trim() === '') return 0;

  const qrCodeUrl = `${baseUrl}${qrCode}`;
  const img = new Image();
  img.crossOrigin = 'Anonymous';

  await new Promise((resolve) => {
    img.onload = resolve;
    img.onerror = resolve; // fail open
    img.src = qrCodeUrl;
  });

  try {
    // If image failed to load, naturalWidth will be 0
    if (!img.naturalWidth) return 0;

    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    const qrBase64 = canvas.toDataURL('image/png');

    doc.setPage(pageNumber);

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const qrSize = QR_SIZE_MM;
    const margin = QR_MARGIN_MM;
    const x = pageWidth - qrSize - margin;
    const anchor = Number.isFinite(contentBottomMm) ? contentBottomMm : 0;
    const y = Math.max(pageHeight - qrSize - margin, anchor + 2);

    doc.addImage(qrBase64, 'PNG', x, y, qrSize, qrSize);
    return y + qrSize + margin;
  } catch (e) {
    return 0;
  }
}

async function renderChallan1IntoDoc(doc, data, renderOptions = {}) {
  const allowAddPage = renderOptions?.allowAddPage !== false;
  const tablePageBreak = renderOptions?.tablePageBreak;
  const tableRowPageBreak = renderOptions?.tableRowPageBreak;

  const {
    qr,
    qrCode,
    clientName,
    items,
    entityId,
    challanCode,
    packagingCost = 0,
    createdAt,
    hideRate = false,
    ad = "",
  } = data;

  const code = getEntityShortCode(entityId);
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginLeft = 2;
  const marginRight = 2;
  const usableWidth = Math.max(40, pageWidth - marginLeft - marginRight);
  const rightX = pageWidth - marginRight;

  let totalQuantity = 0;
  let totalAmount = 0;

  const rows = (items || []).map((o, index) => {
    const {
      nameR,
      nameL,
      lProductCode,
      rProductCode,
      rate,
      quantity,
      amount,
    } = o;

    totalQuantity += Number(quantity);
    totalAmount += Number(amount);

    const desc = `${nameR ? `${nameR} (${rProductCode?.substring(3)})` : ''}${nameL ? `\n${nameL} (${lProductCode?.substring(3)})` : ''}`;

    if (hideRate) {
      return [index + 1, desc, quantity];
    }

    return [index + 1, desc, quantity, rate, String(roundMoney(amount))];
  });

  const totalAmountWithPackaging = roundMoney(totalAmount + Number(packagingCost));
  const totalAmountInWords = inWords2(totalAmountWithPackaging);

  // Header
  const headerY = 8;
  const headerCodeY = 12;
  const headerLine2Y = 16;

  const parts = String(challanCode || '').split('-');
  const challanShort = parts.length >= 4 ? `${parts[0]}/${parts[3]}` : String(challanCode || '');

  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.text(`Date: ${createdAt || ''}`, 5, headerY, { fontStyle: 'normal' });

  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text(`${code} ${challanShort}`, rightX, headerCodeY, {
    align: 'right',
    fontStyle: 'bold',
  });

  doc.setFontSize(12);
  doc.text(`M/S: ${clientName || ''}`, 5, headerLine2Y, { fontStyle: 'bold' });

  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.1);
  doc.line(marginLeft, headerLine2Y + 1, rightX, headerLine2Y + 1);

  // Table
  const headers = hideRate
    ? [['#', 'Product Description', 'Qty']]
    : [['#', 'Product Description', 'Qty', 'Net Rate', 'Amt']];

  // Column widths must fit thermal width; compute from usableWidth to avoid right-side clipping.
  const columnStyles = hideRate
    ? (() => {
      const c0 = 6;
      const c2 = 10;
      const c1 = Math.max(20, usableWidth - c0 - c2);
      return {
        0: { cellWidth: c0, halign: 'center' },
        1: { cellWidth: c1, halign: 'left' },
        2: { cellWidth: c2, halign: 'center' },
      };
    })()
    : (() => {
      const c0 = 6; // #
      const c2 = 7; // Qty
      const c3 = 11; // Net Rate
      const c4 = 12; // Amt
      const c1 = Math.max(18, usableWidth - c0 - c2 - c3 - c4); // Description
      return {
        0: { cellWidth: c0, halign: 'center' },
        1: { cellWidth: c1, halign: 'left' },
        2: { cellWidth: c2, halign: 'center' },
        3: { cellWidth: c3, halign: 'right' },
        4: { cellWidth: c4, halign: 'right' },
      };
    })();

  const firstPageNumberForThisChallan = doc.internal.getNumberOfPages();

  doc.autoTable({
    margin: { left: marginLeft, right: marginRight, top: 18 },
    head: headers,
    body: rows,
    startY: 18,
    showFoot: 'never',
    theme: 'plain',
    rowPageBreak: 'auto',
    styles: {
      fontSize: 9,
      textColor: [0, 0, 0],
      lineWidth: 0.1,
      lineColor: [0, 0, 0],
      fillColor: [255, 255, 255],
      fontStyle: 'bold',
      cellPadding: { top: 1, bottom: 1, left: 1, right: 1 },
      minCellHeight: 3,
      overflow: 'linebreak',
      valign: 'top',
    },
    headStyles: {
      fontStyle: 'bold',
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
      valign: 'middle',
      lineWidth: 0.2,
      lineColor: [0, 0, 0],
      fontSize: 9,
      cellPadding: { top: 1.5, bottom: 1.5, left: 1, right: 1 },
    },
    columnStyles,
    tableWidth: usableWidth,
    tableLineWidth: 0.1,
    tableLineColor: [0, 0, 0],
    didDrawPage: function (tableCtx) {
      // Print the header again on subsequent pages of THIS challan
      if (tableCtx.pageNumber > 1) {
        doc.setFillColor(255, 255, 255);
        doc.rect(0, 0, doc.internal.pageSize.getWidth(), 16, 'F');

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text(`Date: ${createdAt || ''}`, 5, 8, { fontStyle: 'normal' });

        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        doc.text(`${code} ${challanShort}`, rightX, 12, { align: 'right', fontStyle: 'bold' });

        doc.setFontSize(12);
        doc.text(`M/S: ${clientName || ''}`, 5, 16, { fontStyle: 'bold' });

        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.1);
        doc.line(marginLeft, 17, rightX, 17);
      }
    },
    didParseCell: function (cellCtx) {
      if (cellCtx.cell.text && cellCtx.cell.text.length > 0) {
        cellCtx.cell.minHeight = 3;
      }
    },
    pageBreak: tablePageBreak || 'auto',
    rowPageBreak: tableRowPageBreak || 'auto',
    alternateRowStyles: { fillColor: [250, 250, 250] },
  });

  const finalY = doc.lastAutoTable?.finalY;
  let bottomY = Number.isFinite(finalY) ? finalY : 0;

  // Totals table
  const totalsBody = hideRate
    ? [[{ content: `Total Qty: ${totalQuantity}`, styles: { halign: 'left', fontStyle: 'bold', fontSize: 10 } }]]
    : [
      [
        { content: 'Packaging Cost', styles: { halign: 'left', fontStyle: 'bold', fontSize: 10 } },
        { content: String(roundMoney(packagingCost)), styles: { halign: 'right', fontStyle: 'bold', fontSize: 10 } },
      ],
      [
        { content: `Total (Qty: ${totalQuantity})`, styles: { halign: 'left', fontStyle: 'bold', fontSize: 10 } },
        { content: String(roundMoney(totalAmount + Number(packagingCost))), styles: { halign: 'right', fontStyle: 'bold', fontSize: 10 } },
      ],
    ];

  doc.autoTable({
    showHead: 'never',
    margin: { left: marginLeft, right: marginRight, top: 1 },
    rowPageBreak: 'avoid',
    theme: 'plain',
    body: totalsBody,
    startY: finalY + 1,
    styles: {
      fontStyle: 'bold',
      fontSize: 10,
      textColor: [0, 0, 0],
      lineColor: [200, 200, 200],
      lineWidth: 0.1,
      fillColor: [255, 255, 255],
      cellPadding: { top: 2, bottom: 2, left: 1, right: 1 },
      minCellHeight: 4,
      valign: 'middle',
    },
    columnStyles: hideRate
      ? { 0: { cellWidth: usableWidth, halign: 'left' } }
      : (() => {
        const amtW = 12;
        const labelW = Math.max(18, usableWidth - amtW);
        return {
          0: { cellWidth: labelW, halign: 'left' },
          1: { cellWidth: amtW, halign: 'right' },
        };
      })(),
    tableLineWidth: 0.1,
    tableLineColor: [200, 200, 200],
  });

  let currentY = (Number.isFinite(doc.lastAutoTable?.finalY) ? doc.lastAutoTable.finalY : bottomY) + 2;
  bottomY = Math.max(bottomY, currentY);
  const lineHeight = 5;

  if (!hideRate) {
    // jsPDF 1.5.x: prefer explicit setFont() over { fontStyle: 'bold' } in doc.text options.
    doc.setFont(undefined, 'bold');
    const pageWidth = doc.internal.pageSize.getWidth();
    const leftMargin = 5;
    const rightMargin = 5;
    const maxTextWidth = pageWidth - leftMargin - rightMargin;
    const amountInWordsText = `Total Amount In Words: ${totalAmountInWords} Rupees Only`;
    const splitText = doc.splitTextToSize(amountInWordsText, maxTextWidth);

    splitText.forEach((line) => {
      doc.text(line, leftMargin, currentY, { halign: 'left' });
      currentY += lineHeight;
    });
    bottomY = Math.max(bottomY, currentY);
    doc.setFont(undefined, 'normal');
  }

  const pageHeight = doc.internal.pageSize.getHeight();
  const footerStartY = currentY + 3;
  if (allowAddPage && footerStartY + 8 > pageHeight) {
    doc.addPage();
    currentY = 20;
  } else {
    currentY = footerStartY;
  }
  bottomY = Math.max(bottomY, currentY + 4);

  doc.setFontSize(9);
  doc.setFont(undefined, 'bold');
  doc.text('E & OE', 5, currentY, { halign: 'left' });
  doc.text('Working Hours: 10.30 AM to 7.00 PM', 5, currentY + 4, { halign: 'left' });
  doc.setFont(undefined, 'normal');

  if (ad && ad.trim() !== '') {
    const pageWidth = doc.internal.pageSize.getWidth();
    const leftMargin = 5;
    const rightMargin = 5;
    const maxTextWidth = pageWidth - leftMargin - rightMargin;
    const splitAdText = doc.splitTextToSize(ad.trim(), maxTextWidth);

    let adY = currentY + 8;
    splitAdText.forEach((line) => {
      doc.text(line, leftMargin, adY, { halign: 'left', fontStyle: 'normal' });
      adY += 4;
    });
    bottomY = Math.max(bottomY, adY);
  }

  // Add QR on the LAST page that belongs to this challan (not the whole document)
  const lastPageNumberForThisChallan = doc.internal.getNumberOfPages();
  const qrBottom = await addQrToSpecificPage(doc, {
    qr,
    qrCode,
    contentBottomMm: bottomY,
    pageNumber: lastPageNumberForThisChallan,
  });
  if (qrBottom > 0) bottomY = Math.max(bottomY, qrBottom);

  return {
    firstPageNumberForThisChallan,
    lastPageNumberForThisChallan,
    bottomY,
  };
}

// 72mm thermal roll: keep PDF width aligned to printer to avoid horizontal clipping.
const THERMAL_WIDTH_MM = 72;
const MEASURE_HEIGHT_MM = 2000; // measurement page height
const PAGE_PADDING_MM = 8; // extra breathing room to avoid edge-case overflow
const MAX_SINGLE_PAGE_HEIGHT_MM = 20000; // safety cap to avoid runaway sizes
/** Short slips: a large min height made single-line challans look tiny when "fit to page width" scales the whole page. */
const MIN_THERMAL_PAGE_HEIGHT_MM = 48;

function setPageSizeMm(doc, widthMm, heightMm) {
  // jsPDF 1.5.x: custom format arrays are interpreted as points, not doc units.
  // Using setWidth/setHeight keeps everything in mm reliably.
  doc.internal.pageSize.setWidth(Number(widthMm));
  doc.internal.pageSize.setHeight(Number(heightMm));
}

/**
 * Thermal challan PDF: one page per challan, height = content (+ padding).
 * Shared by single print and multi-print so short receipts are not laid out on a tall A6 canvas.
 */
async function buildChallan1ThermalPdfBlob(challansData) {
  try {
    const list = (Array.isArray(challansData) ? challansData : []).filter(Boolean);

    if (list.length === 0) {
      const emptyDoc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a6',
        putOnlyUsedFonts: true,
        compress: true,
        hotfixes: ['px_scaling'],
      });
      setPageSizeMm(emptyDoc, THERMAL_WIDTH_MM, MIN_THERMAL_PAGE_HEIGHT_MM);
      return emptyDoc.output('blob');
    }

    let doc = null;

    for (let idx = 0; idx < list.length; idx++) {
      const challan = list[idx];

      const measureDoc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a6',
        putOnlyUsedFonts: true,
        compress: true,
        hotfixes: ['px_scaling'],
      });
      setPageSizeMm(measureDoc, THERMAL_WIDTH_MM, MEASURE_HEIGHT_MM);

      // eslint-disable-next-line no-await-in-loop
      const measured = await renderChallan1IntoDoc(measureDoc, challan, {
        allowAddPage: false,
        tablePageBreak: 'auto',
        tableRowPageBreak: 'auto',
      });

      const bottomY = Number.isFinite(measured?.bottomY) ? measured.bottomY : MIN_THERMAL_PAGE_HEIGHT_MM;
      const measuredPages = Number(measureDoc.internal.getNumberOfPages?.() || 1) || 1;

      const rawHeight = (measuredPages - 1) * MEASURE_HEIGHT_MM + bottomY + PAGE_PADDING_MM;
      const requiredHeight = Math.min(
        MAX_SINGLE_PAGE_HEIGHT_MM,
        Math.max(MIN_THERMAL_PAGE_HEIGHT_MM, rawHeight)
      );

      if (!doc) {
        doc = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a6',
          putOnlyUsedFonts: true,
          compress: true,
          hotfixes: ['px_scaling'],
        });
        setPageSizeMm(doc, THERMAL_WIDTH_MM, requiredHeight);
      } else {
        doc.addPage('a6', 'p');
        setPageSizeMm(doc, THERMAL_WIDTH_MM, requiredHeight);
      }

      // eslint-disable-next-line no-await-in-loop
      await renderChallan1IntoDoc(doc, challan, {
        allowAddPage: false,
        tablePageBreak: 'auto',
        tableRowPageBreak: 'auto',
      });
    }

    const blob = doc.output('blob');
    if (!(blob instanceof Blob)) {
      throw new Error('Thermal PDF generation returned an invalid blob.');
    }
    return blob;
  } catch (e) {
    const msg = e?.message ? String(e.message) : String(e || 'Unknown error');
    throw new Error(`Thermal PDF generation failed: ${msg}`);
  }
}

export const challan1Template = (data) => buildChallan1ThermalPdfBlob([data]);

// Thermal multi-print: each challan stays continuous; page break occurs between challans.
export const challan1TemplateMulti = (challansData) => buildChallan1ThermalPdfBlob(challansData);